import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Server, RefreshCw, Search, X, Trash2, AlertCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { LoadingState, ErrorState } from '@/components/ui/loading-state'
import { cn } from '@/lib/utils'
import { getRedisMetrics, searchRedisKeys, getRedisKeyDetail, expireRedisKey } from '../api/redisConsole'
import type { RedisKeyEntry, RedisKeysPage } from '../types/redisConsole.types'

// The backend serializes with spring.jackson.default-property-inclusion=non_null, which OMITS
// null fields from the JSON entirely rather than sending `null` — so an absent optional field
// comes through here as `undefined`, not `null`. Every null-check below uses `==`/`!=` (loose)
// against null specifically to catch both, since `undefined !== null` is true and would otherwise
// slip past a strict check straight into a crash (e.g. calling .toLocaleString() on undefined).

function formatBytes(bytes: number | null | undefined): string {
  if (bytes == null) return '—'
  if (bytes < 1024) return `${bytes} B`
  const units = ['KB', 'MB', 'GB', 'TB']
  let value = bytes
  let unitIndex = -1
  do {
    value /= 1024
    unitIndex++
  } while (value >= 1024 && unitIndex < units.length - 1)
  return `${value.toFixed(1)} ${units[unitIndex]}`
}

function formatUptime(seconds: number | null | undefined): string {
  if (seconds == null) return '—'
  const days = Math.floor(seconds / 86400)
  const hours = Math.floor((seconds % 86400) / 3600)
  if (days > 0) return `${days}d ${hours}h`
  const minutes = Math.floor((seconds % 3600) / 60)
  return `${hours}h ${minutes}m`
}

function formatTtl(seconds: number | null | undefined): string {
  if (seconds == null) return '—'
  if (seconds < 0) return 'no expiry'
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  return h > 0 ? `${h}h ${m}m` : `${m}m`
}

function MetricCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-3">
      <div className="text-xs text-gray-500 font-medium mb-1">{label}</div>
      <div className="text-lg font-bold text-gray-900 font-mono">{value}</div>
    </div>
  )
}

export function RedisConsolePage() {
  const queryClient = useQueryClient()
  const [pattern, setPattern] = useState('*')
  const [activeSearch, setActiveSearch] = useState<{ pattern: string; page: number } | null>(null)
  const [pages, setPages] = useState<RedisKeysPage[]>([])
  const [selectedKey, setSelectedKey] = useState<string | null>(null)
  const [expireTarget, setExpireTarget] = useState<string | null>(null)

  const metrics = useQuery({
    queryKey: ['redis-metrics'],
    queryFn: getRedisMetrics,
    refetchInterval: 15_000,
  })

  const searchMutation = useMutation({
    mutationFn: ({ pattern: p, cursor }: { pattern: string; cursor: string }) => searchRedisKeys(p, cursor, 100),
    onSuccess: (result) => setPages((prev) => [...prev, result]),
  })

  const handleSearch = () => {
    const trimmed = pattern.trim() || '*'
    setPages([])
    setActiveSearch({ pattern: trimmed, page: 0 })
    searchMutation.mutate({ pattern: trimmed, cursor: '0' })
  }

  const handleLoadMore = () => {
    if (!activeSearch || pages.length === 0) return
    const lastPage = pages[pages.length - 1]
    searchMutation.mutate({ pattern: activeSearch.pattern, cursor: lastPage.nextCursor })
  }

  const allKeys: RedisKeyEntry[] = pages.flatMap((p) => p.keys)
  const lastPage = pages[pages.length - 1]
  const canLoadMore = !!lastPage && !lastPage.complete

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-red-50">
            <Server className="h-5 w-5 text-red-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Redis Console</h1>
            <p className="text-sm text-gray-500 mt-0.5">
              Search, inspect, and force-expire keys on the shared Redis instance. SCAN-based search never blocks
              the instance the way <code className="text-xs bg-gray-100 px-1 rounded">KEYS *</code> would.
            </p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={() => metrics.refetch()} disabled={metrics.isFetching}>
          <RefreshCw className={cn('h-4 w-4 mr-1.5', metrics.isFetching && 'animate-spin')} />
          Refresh Metrics
        </Button>
      </div>

      {/* Metrics */}
      {metrics.isLoading ? (
        <LoadingState message="Loading Redis metrics…" />
      ) : metrics.isError ? (
        <ErrorState message={(metrics.error as Error).message} onRetry={() => metrics.refetch()} />
      ) : metrics.data ? (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
          <MetricCard label="Total Keys" value={metrics.data.totalKeys.toLocaleString()} />
          <MetricCard label="Memory Used" value={metrics.data.usedMemoryHuman ?? formatBytes(metrics.data.usedMemoryBytes)} />
          <MetricCard
            label="Ops/sec"
            value={metrics.data.opsPerSecond != null ? metrics.data.opsPerSecond.toFixed(0) : 'n/a'}
          />
          <MetricCard label="Clients" value={metrics.data.connectedClients?.toLocaleString() ?? '—'} />
          <MetricCard label="Uptime" value={formatUptime(metrics.data.uptimeSeconds)} />
          <MetricCard label="Version" value={metrics.data.redisVersion ?? '—'} />
          <MetricCard label="Role" value={metrics.data.role ?? '—'} />
        </div>
      ) : null}

      {/* Search */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex gap-2 items-end">
          <div className="flex-1">
            <label className="block text-xs font-medium text-gray-500 mb-1.5">
              Pattern (glob-style, e.g. <code className="bg-gray-100 px-1 rounded">bal:1234:*</code>)
            </label>
            <Input
              value={pattern}
              onChange={(e) => setPattern(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              placeholder="*"
            />
          </div>
          <Button onClick={handleSearch} disabled={searchMutation.isPending}>
            <Search className="h-4 w-4 mr-1.5" />
            Search
          </Button>
        </div>
      </div>

      {/* Results */}
      {activeSearch && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
            <span className="text-sm text-gray-600">
              {allKeys.length.toLocaleString()} key{allKeys.length !== 1 ? 's' : ''} found
              {!canLoadMore && pages.length > 0 && ' (scan complete)'}
            </span>
          </div>

          {searchMutation.isPending && pages.length === 0 ? (
            <div className="p-8">
              <LoadingState message="Scanning…" />
            </div>
          ) : searchMutation.isError ? (
            <div className="p-8">
              <ErrorState message={(searchMutation.error as Error).message} onRetry={handleSearch} />
            </div>
          ) : allKeys.length === 0 ? (
            <div className="p-12 text-center text-gray-500 text-sm">No keys match this pattern.</div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 bg-gray-50">
                      <th className="text-left px-4 py-2.5 font-medium text-gray-600">Key</th>
                      <th className="text-left px-4 py-2.5 font-medium text-gray-600 whitespace-nowrap">Type</th>
                      <th className="text-left px-4 py-2.5 font-medium text-gray-600 whitespace-nowrap">TTL</th>
                      <th className="text-right px-4 py-2.5 font-medium text-gray-600 whitespace-nowrap">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allKeys.map((entry) => (
                      <tr key={entry.key} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="px-4 py-2.5 font-mono text-xs text-gray-900 break-all">{entry.key}</td>
                        <td className="px-4 py-2.5 whitespace-nowrap">
                          <Badge variant="secondary">{entry.type}</Badge>
                        </td>
                        <td className="px-4 py-2.5 text-gray-500 whitespace-nowrap">{formatTtl(entry.ttlSeconds)}</td>
                        <td className="px-4 py-2.5 text-right whitespace-nowrap">
                          <div className="flex gap-1.5 justify-end">
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 px-2 text-xs"
                              onClick={() => setSelectedKey(entry.key)}
                            >
                              View
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 px-2 text-xs text-red-600 hover:text-red-700 gap-1"
                              onClick={() => setExpireTarget(entry.key)}
                            >
                              <Trash2 className="h-3 w-3" /> Expire
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {canLoadMore && (
                <div className="p-4 text-center border-t border-gray-100">
                  <Button variant="outline" size="sm" onClick={handleLoadMore} disabled={searchMutation.isPending}>
                    {searchMutation.isPending ? 'Loading…' : 'Load More'}
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {selectedKey && (
        <KeyDetailModal
          redisKey={selectedKey}
          onClose={() => setSelectedKey(null)}
          onExpire={() => {
            setExpireTarget(selectedKey)
            setSelectedKey(null)
          }}
        />
      )}

      {expireTarget && (
        <ExpireKeyModal
          redisKey={expireTarget}
          onClose={() => setExpireTarget(null)}
          onExpired={() => {
            setExpireTarget(null)
            setPages((prev) =>
              prev.map((p) => ({ ...p, keys: p.keys.filter((k) => k.key !== expireTarget) })),
            )
            queryClient.invalidateQueries({ queryKey: ['redis-metrics'] })
          }}
        />
      )}
    </div>
  )
}

// ── Key detail modal ─────────────────────────────────────────────────────────

function KeyDetailModal({
  redisKey,
  onClose,
  onExpire,
}: {
  redisKey: string
  onClose: () => void
  onExpire: () => void
}) {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['redis-key-detail', redisKey],
    queryFn: () => getRedisKeyDetail(redisKey),
  })

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl mx-4 max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b">
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-gray-900 font-mono break-all">{redisKey}</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 shrink-0 ml-3">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-5">
          {isLoading ? (
            <LoadingState message="Loading key…" />
          ) : isError ? (
            <ErrorState message={(error as Error).message} />
          ) : !data ? (
            <p className="text-sm text-gray-500">Key not found — it may have expired or been deleted.</p>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3 text-sm">
                <div>
                  <div className="text-xs text-gray-500 mb-0.5">Type</div>
                  <Badge variant="secondary">{data.type}</Badge>
                </div>
                <div>
                  <div className="text-xs text-gray-500 mb-0.5">TTL</div>
                  <div className="font-mono">{formatTtl(data.ttlSeconds)}</div>
                </div>
                {data.size != null && (
                  <div>
                    <div className="text-xs text-gray-500 mb-0.5">Size</div>
                    <div className="font-mono">{data.size.toLocaleString()} elements</div>
                  </div>
                )}
              </div>
              {data.truncated && (
                <p className="text-xs text-amber-600 flex items-center gap-1.5">
                  <AlertCircle className="h-3.5 w-3.5" />
                  Showing first 200 elements only — the full collection is larger.
                </p>
              )}
              <div>
                <div className="text-xs text-gray-500 mb-1.5">Value</div>
                <pre className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-xs font-mono overflow-x-auto max-h-96 overflow-y-auto whitespace-pre-wrap break-all">
                  {typeof data.value === 'string' ? data.value : JSON.stringify(data.value, null, 2)}
                </pre>
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 p-5 border-t">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
          {data && (
            <Button variant="destructive" onClick={onExpire}>
              <Trash2 className="h-4 w-4 mr-1.5" />
              Force Expire
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}

// ── Expire confirmation modal ─────────────────────────────────────────────────

function ExpireKeyModal({
  redisKey,
  onClose,
  onExpired,
}: {
  redisKey: string
  onClose: () => void
  onExpired: () => void
}) {
  const [reason, setReason] = useState('')

  const mutation = useMutation({
    mutationFn: () => expireRedisKey(redisKey, reason.trim()),
    onSuccess: onExpired,
  })

  const isValid = reason.trim().length > 0

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4">
        <div className="flex items-center justify-between p-5 border-b">
          <h2 className="text-lg font-semibold text-gray-900">Force Expire Key</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3">
            <AlertCircle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
            <p className="text-xs text-amber-800">
              This deletes the key immediately — irreversible. If this is a{' '}
              <code className="bg-amber-100 px-1 rounded">bal:*</code> key, the next send check will lazy-reload a
              fresh value from account_consumables instead of waiting out the remaining TTL.
            </p>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1.5">Key</label>
            <div className="font-mono text-xs bg-gray-50 border border-gray-200 rounded-lg p-2 break-all">
              {redisKey}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Reason <span className="text-red-500">*</span>
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Why is this key being force-expired? This becomes the permanent audit record."
              maxLength={1000}
              rows={3}
              required
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring resize-none"
            />
          </div>

          {mutation.isError && (
            <p className="text-sm text-red-600 bg-red-50 rounded px-3 py-2">{(mutation.error as Error).message}</p>
          )}
        </div>

        <div className="flex justify-end gap-3 p-5 border-t">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="destructive" disabled={!isValid || mutation.isPending} onClick={() => mutation.mutate()}>
            {mutation.isPending ? 'Expiring…' : 'Force Expire'}
          </Button>
        </div>
      </div>
    </div>
  )
}
